import React, { useEffect, useState, useMemo } from 'react';
import { Rect, Text, Line, Group } from 'react-konva'; // Removed Image as KonvaImage
import {
  LabelElement,
  isImageElement,
  isTableElement,
  isAdvancedTableElement,
  AdvancedTableCellData,
} from '../../types';
import useImage from '../../hooks/useImage';
import { useElementImageGenerator } from '../../hooks/useElementImageGenerator';
import AdvancedTableElement from './elements/AdvancedTableElement';
import RenderedElementContent from './RenderedElementContent';
import { SalesOrder, SalesOrderItem } from 'services/salesOrders';
import { getSOValueAtPath } from '../../helpers';
import { COMMON_RENDERED_TYPES } from '../properties/consts';

interface ChildRendererProps {
  child: LabelElement;
  previewMode?: boolean;
  previewData: SalesOrder | null;
}

const ChildRenderer: React.FC<ChildRendererProps> = ({
  child,
  previewMode = false,
  previewData,
}) => {
  const [image] = useImage(isImageElement(child) ? child.src ?? null : null);
  const {
    barcodeImageUrl,
    qrImageUrl,
    isLoading: _isLoading,
    error,
  } = useElementImageGenerator({
    element: child,
    previewMode,
    previewData,
  });

  const [konvaBarcodeImage, setKonvaBarcodeImage] =
    useState<HTMLImageElement | null>(null);
  const [konvaQrImage, setKonvaQrImage] = useState<HTMLImageElement | null>(
    null
  );

  const processedAdvancedTableData = useMemo(() => {
    if (
      previewMode &&
      isAdvancedTableElement(child) &&
      child.advancedTableData &&
      previewData?.salesItems
    ) {
      const originalTableData = child.advancedTableData;
      const rowCount = previewData.salesItems.length;
      const headers = originalTableData.headers;
      const tableData: AdvancedTableCellData[][] = [];

      if (headers) {
        previewData.salesItems.forEach((item: SalesOrderItem) => {
          const rowData: AdvancedTableCellData[] = [];
          headers.forEach((header) => {
            const content = getSOValueAtPath(
              item,
              header.key || header.path || ''
            );
            rowData.push({
              content: content?.toString() ?? '',
            });
          });
          tableData.push(rowData);
        });
      }

      return {
        ...originalTableData,
        numRows: rowCount,
        numColumns: headers?.length ?? 0,
        rowHeights: Array(rowCount).fill(
          originalTableData.rowHeights?.[0] || 18
        ),
        data: tableData,
      };
    }
    if (isAdvancedTableElement(child)) {
      return child.advancedTableData;
    }
    return null;
  }, [child, previewMode, previewData]);

  useEffect(() => {
    let isMounted = true;
    if (barcodeImageUrl) {
      const img = new window.Image();
      img.onload = () => {
        if (isMounted) {
          setKonvaBarcodeImage(img);
        }
      };
      img.src = barcodeImageUrl;
    } else {
      if (isMounted) {
        setKonvaBarcodeImage(null);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [barcodeImageUrl]);

  useEffect(() => {
    let isMounted = true;
    if (qrImageUrl) {
      const img = new window.Image();
      img.onload = () => {
        if (isMounted) {
          setKonvaQrImage(img);
        }
      };
      img.src = qrImageUrl;
    } else {
      if (isMounted) {
        setKonvaQrImage(null);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [qrImageUrl]);

  useEffect(() => {
    if (error) {
      console.error('Error generating image via hook:', error);
      // Optionally, set some state to display an error placeholder in the canvas
    }
  }, [error]);

  const commonProps = {
    x: child.x,
    y: child.y,
    width: child.width,
    height: child.height,
    rotation: child.rotation || 0,
    onMouseEnter: (e: any) => {
      const container = e.target.getStage().container();
      container.style.cursor = 'move';
    },
    onMouseLeave: (e: any) => {
      const container = e.target.getStage().container();
      container.style.cursor = 'default';
    },
  };

  const renderTable = () => {
    if (!isTableElement(child)) return null;

    const tableData = child.tableData || {
      rows: 3,
      columns: 3,
      cellWidth: child.width / 3,
      cellHeight: child.height / 3,
      headerRow: false,
      headerRowHeight: 40,
      headerFontSize: 16,
      headerFontFamily: 'Arial',
      headerBackgroundColor: '#f0f0f0',
      headerTextColor: '#000000',
      headerTexts: Array(3).fill('Header'),
      borderColor: child.stroke || '#000000',
      borderWidth: child.lineWidth || 1,
      showGrid: true,
      backgroundColor: '#ffffff',
      highlightedRows: [],
      highlightedColumns: [],
      highlightColor: undefined,
    };

    const cellWidth = tableData.cellWidth ?? child.width / tableData.columns;
    const cellHeight = tableData.cellHeight ?? child.height / tableData.rows;

    const cells = [];

    cells.push(
      <Rect
        key="draggable-area"
        x={0}
        y={0}
        width={child.width}
        height={child.height}
        fill="transparent"
      />
    );

    if (tableData.backgroundColor) {
      cells.push(
        <Rect
          key="background"
          x={0}
          y={0}
          width={child.width}
          height={child.height}
          fill={tableData.backgroundColor}
        />
      );
    }

    if (tableData.headerRow) {
      cells.push(
        <Rect
          key="header-background"
          x={0}
          y={0}
          width={child.width}
          height={tableData.headerRowHeight || cellHeight}
          fill={tableData.headerBackgroundColor || '#f0f0f0'}
        />
      );

      const headerTexts =
        tableData.headerTexts || Array(tableData.columns).fill('Header');
      headerTexts.forEach((text, index) => {
        cells.push(
          <Text
            key={`header-text-${index}`}
            x={index * (cellWidth || child.width / tableData.columns)}
            y={0}
            width={cellWidth || child.width / tableData.columns}
            height={tableData.headerRowHeight || cellHeight}
            text={text}
            fontSize={tableData.headerFontSize || 16}
            fontFamily={tableData.headerFontFamily || 'Arial'}
            fill={tableData.headerTextColor || '#000000'}
            align="center"
            verticalAlign="middle"
          />
        );
      });
    }

    if (tableData.highlightedRows?.length) {
      tableData.highlightedRows.forEach((rowIndex) => {
        const yOffset =
          tableData.headerRow && rowIndex > 0
            ? (tableData.headerRowHeight ||
                cellHeight ||
                child.height / tableData.rows) +
              (rowIndex - 1) * (cellHeight || child.height / tableData.rows)
            : rowIndex * (cellHeight || child.height / tableData.rows);

        cells.push(
          <Rect
            key={`highlight-row-${rowIndex}`}
            x={0}
            y={yOffset}
            width={child.width}
            height={cellHeight || child.height / tableData.rows}
            fill={tableData.highlightColor || '#f0f0f0'}
          />
        );
      });
    }

    if (tableData.highlightedColumns?.length) {
      tableData.highlightedColumns.forEach((colIndex) => {
        cells.push(
          <Rect
            key={`highlight-col-${colIndex}`}
            x={colIndex * (cellWidth || child.width / tableData.columns)}
            y={0}
            width={cellWidth || child.width / tableData.columns}
            height={child.height}
            fill={tableData.highlightColor || '#f0f0f0'}
          />
        );
      });
    }

    if (tableData.showGrid) {
      for (let row = 0; row <= tableData.rows; row++) {
        const y =
          row === 1 && tableData.headerRow
            ? tableData.headerRowHeight ||
              cellHeight ||
              child.height / tableData.rows
            : row * (cellHeight || child.height / tableData.rows);

        cells.push(
          <Line
            key={`h-${row}`}
            points={[0, y, child.width, y]}
            stroke={tableData.borderColor}
            strokeWidth={tableData.borderWidth}
          />
        );
      }

      for (let col = 0; col <= tableData.columns; col++) {
        cells.push(
          <Line
            key={`v-${col}`}
            points={[
              col * (cellWidth || child.width / tableData.columns),
              0,
              col * (cellWidth || child.width / tableData.columns),
              child.height,
            ]}
            stroke={tableData.borderColor}
            strokeWidth={tableData.borderWidth}
          />
        );
      }
    } else {
      cells.push(
        <Rect
          key="outer-border"
          x={0}
          y={0}
          width={child.width}
          height={child.height}
          stroke={tableData.borderColor}
          strokeWidth={tableData.borderWidth}
          fill="transparent"
        />
      );
    }

    return <Group {...commonProps}>{cells}</Group>;
  };

  const renderElement = () => {
    if (COMMON_RENDERED_TYPES.includes(child.type)) {
      let imageProps = {};
      if (child.type === 'barcode') {
        imageProps = { konvaBarcodeImage };
      } else if (child.type === 'qrcode') {
        imageProps = { konvaQrImage };
      } else if (child.type === 'image') {
        imageProps = { image };
      }

      return (
        <RenderedElementContent
          element={child}
          commonProps={commonProps}
          previewMode={previewMode}
          previewData={previewData}
          {...imageProps}
        />
      );
    } else if (child.type === 'table' && isTableElement(child)) {
      return renderTable();
    } else if (
      child.type === 'advancedTable' &&
      isAdvancedTableElement(child)
    ) {
      return (
        <AdvancedTableElement
          {...commonProps}
          id={child.id}
          type={child.type}
          advancedTableData={processedAdvancedTableData ?? undefined}
          previewMode={previewMode}
        />
      );
    }

    return null;
  };

  const renderedElement = renderElement();
  return renderedElement ? <>{renderedElement}</> : null;
};

export default ChildRenderer;
